import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Chip,
    Alert,
    Container,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    BarChart as BarChartIcon,
    PlayArrow
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function AdminLearningPathsPage() {
    const navigate = useNavigate();
    const [paths, setPaths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pathStats, setPathStats] = useState({});
    const [selectedPath, setSelectedPath] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const pathsData = await learningService.getLearningPaths(false);
                setPaths(pathsData);

                // Fetch stats for each path
                const stats = {};
                for (const path of pathsData) {
                    stats[path.id] = await learningService.getPathStats(path.id);
                }
                setPathStats(stats);
            } catch (error) {
                console.error("Error fetching learning paths:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleDelete = async () => {
        if (!selectedPath) return;
        try {
            // Note: Add delete function to learningService if needed
            setPaths(paths.filter((p) => p.id !== selectedPath.id));
            setOpenDeleteDialog(false);
            setSelectedPath(null);
        } catch (error) {
            console.error("Error deleting path:", error);
        }
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            Beginner: "success",
            Intermediate: "warning",
            Advanced: "error"
        };
        return colors[difficulty] || "default";
    };

    if (loading) {
        return (
            <AdminLayout title="Learning Paths">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Learning Paths">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            📚 Learning Paths Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create, manage, and monitor learning paths for students
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/admin/learning-paths/create")}
                        size="large"
                    >
                        Create New Path
                    </Button>
                </Box>

                {/* Stats Overview */}
                {paths.length > 0 && (
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: "center" }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
                                        {paths.length}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Paths
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: "center" }}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 700, color: "success.main" }}
                                    >
                                        {Object.values(pathStats).reduce((sum, s) => sum + s.completedCount, 0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Completions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: "center" }}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 700, color: "warning.main" }}
                                    >
                                        {Object.values(pathStats).reduce((sum, s) => sum + s.totalEnrolled, 0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Enrollments
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ textAlign: "center" }}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 700, color: "info.main" }}
                                    >
                                        {paths.reduce((sum, p) => sum + (p.levels?.length || 0), 0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Levels
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Learning Paths Grid */}
                {paths.length === 0 ? (
                    <Alert severity="info" sx={{ textAlign: "center", py: 4 }}>
                        <Typography>No learning paths created yet.</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate("/admin/learning-paths/create")}
                            sx={{ mt: 2 }}
                        >
                            Create First Path
                        </Button>
                    </Alert>
                ) : (
                    <Grid container spacing={3}>
                        {paths.map((path) => {
                            const stats = pathStats[path.id] || {
                                totalEnrolled: 0,
                                completedCount: 0,
                                completionRate: 0
                            };

                            return (
                                <Grid item xs={12} sm={6} md={4} key={path.id}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                transform: "translateY(-8px)",
                                                boxShadow: 6
                                            },
                                            overflow: "hidden"
                                        }}
                                    >
                                        {/* Header with Difficulty Badge */}
                                        <Box
                                            sx={{
                                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                color: "white",
                                                p: 2
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "flex-start",
                                                    mb: 1
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        flex: 1,
                                                        wordBreak: "break-word"
                                                    }}
                                                >
                                                    {path.name}
                                                </Typography>
                                                <Chip
                                                    label={path.difficulty}
                                                    size="small"
                                                    sx={{
                                                        background:
                                                            path.difficulty === "Beginner"
                                                                ? "rgba(76, 175, 80, 0.3)"
                                                                : path.difficulty === "Intermediate"
                                                                    ? "rgba(255, 193, 7, 0.3)"
                                                                    : "rgba(244, 67, 54, 0.3)",
                                                        color: "white"
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                                {path.creditsOnCompletion} Credits • {path.levels?.length || 0} Levels
                                            </Typography>
                                        </Box>

                                        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                            {/* Description */}
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mb: 2,
                                                    minHeight: 40,
                                                    maxHeight: 60,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical"
                                                }}
                                            >
                                                {path.description}
                                            </Typography>

                                            {/* Stats Section */}
                                            <Stack spacing={1}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        p: 1.5,
                                                        background: "linear-gradient(135deg, #f5f7fa 0%, #eff2f5 100%)",
                                                        borderRadius: 1
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Enrollments
                                                        </Typography>
                                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                            {stats.totalEnrolled}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: "right" }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Completed
                                                        </Typography>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{ fontWeight: 700, color: "success.main" }}
                                                        >
                                                            {stats.completedCount}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: "right" }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Rate
                                                        </Typography>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{ fontWeight: 700, color: "info.main" }}
                                                        >
                                                            {Math.round(stats.completionRate)}%
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Stack>

                                            {/* Duration & Active Status */}
                                            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                <Chip
                                                    label={`⏱️ ${path.duration}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={path.active ? "✅ Active" : "❌ Inactive"}
                                                    size="small"
                                                    color={path.active ? "success" : "default"}
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </CardContent>

                                        {/* Action Buttons */}
                                        <CardActions sx={{ pt: 1, gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<BarChartIcon />}
                                                onClick={() =>
                                                    navigate(`/admin/learning-paths/${path.id}/scoreboard`)
                                                }
                                                fullWidth
                                            >
                                                Scoreboard
                                            </Button>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() =>
                                                    navigate(`/admin/learning-paths/${path.id}/edit`)
                                                }
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                    setSelectedPath(path);
                                                    setOpenDeleteDialog(true);
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Container>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Delete Learning Path</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{selectedPath?.name}</strong>?
                    </Typography>
                    <Typography variant="caption" color="error" sx={{ mt: 2, display: "block" }}>
                        ⚠️ This action cannot be undone. All student progress will be affected.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminLayout>
    );
}
